import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { emptyOnForbidden, supportQueryRetry } from '../../utils/Support/supportHelpers';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS } from './supportQueryHelpers';

export interface ISupportAgentTicketCount {
	agentId: string;
	/** Tickets assigned to this agent that are not Closed, Spam or Archive. */
	openTickets: number;
	/** The agent's own cap, when one is set; the mailbox default applies otherwise. */
	maxTickets?: number | null;
}

export interface ISupportAgentTicketCounts {
	mailboxId?: string;
	unassigned: number;
	agents: ISupportAgentTicketCount[];
}

/** Sentinel for the unassigned bucket, matching the assignee filter's own "no assignee" value. */
export const UNASSIGNED_COUNT_KEY = '';

export type SupportAgentTicketCountMap = Record<string, number>;

/**
 * Open-ticket load per agent for a mailbox. Separate from the roster query so it can refresh on the
 * cadence of ticket activity without re-reading team membership, which barely changes.
 */
export function useSupportAgentTicketCountsQuery(mailboxId: string) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.mailboxes.agentTicketCounts(mailboxId),
		staleTime: 30_000,
		retry: supportQueryRetry,
		queryFn: () =>
			emptyOnForbidden(
				() =>
					userSession.webServiceHelper.callAsync<ISupportAgentTicketCounts>(
						SUPPORT_PATHS.mailboxes.agentTicketCounts(mailboxId),
						'GET'
					),
				{ unassigned: 0, agents: [] }
			),
		enabled: !!mailboxId,
		// A workload number is a convenience on top of the assignee pickers, so a failure is left to
		// render them without counts rather than raising a toast over a list the agent came for.
		meta: { toastErrorMessage: '' },
	});
}

/** agentId → open count, with the unassigned bucket under {@link UNASSIGNED_COUNT_KEY}. */
export function toAgentTicketCountMap(counts: ISupportAgentTicketCounts | undefined): SupportAgentTicketCountMap {
	if (!counts) return {};
	const map: SupportAgentTicketCountMap = { [UNASSIGNED_COUNT_KEY]: counts.unassigned };
	for (const agent of counts.agents) {
		if (agent.agentId) map[agent.agentId] = agent.openTickets;
	}
	return map;
}
